import { useCallback, useEffect, useState, useRef } from "react";
import { useDeviceDetection } from "../../../../hooks/UseDeviceDetection";
import { useViewInfo } from "../../../../hooks/UseViewInfo";
import { Flex, View } from "@adobe/react-spectrum";
import { useDependency } from "../../../../contexts/DependencyProvider";
import { CustomView } from "../../../../services/soap/dataObjects/CustomView";
import { useTranslation } from "react-i18next";
import FilterViewsComponent from "./FilterViewsComponent";
import FilterControlsComponent from "./FilterControlsComponent";
import QuickFilterControlsComponent from "./QuickFilterControlsComponent";
import styles from "./filter_component.module.css";
import { IFilterControl } from "../../../../components/protected/Dialogs/components/FilterSetupPlaceholder/FilterSetupPlaceholder";
import { SettingItem } from "../../../../services/soap/features/responses/GetFormSettingsResponse";
import { Order } from "../../../../services/soap/dataObjects/Order";

interface IFilterComponentProps {
    onFilterChange: (filterParams: {}) => void;
    filterParams: any;
    filterStyle?: any;
    formSettings?: SettingItem[];
    customViews?: CustomView[];
    hasMoreCustomViews?: boolean;
    onLoadMoreCustomViews?: () => void;
    onRefreshCustomViews?: () => void;
    fpaTypeCategory?: string;
    initialViewId?: string;
    onCreateView?: () => void;
}

interface FilterParam {
    name: string;
    type: string;
    value: string;
}

interface FilterParamsState {
    FILTER: { COL: FilterParam[] };
    FIELDS?: { field: string[] };
    ORDER?: Order;
}

const FilterComponent: React.FC<IFilterComponentProps> = ({
    onFilterChange,
    filterParams,
    filterStyle,
    formSettings,
    customViews = [],
    hasMoreCustomViews = false,
    onLoadMoreCustomViews,
    onRefreshCustomViews,
    fpaTypeCategory,
    initialViewId,
    onCreateView,
}) => {
    const { store } = useDependency();
    const { t } = useTranslation();
    const { isMobile } = useViewInfo();
    const deviceInfo = useDeviceDetection(isMobile);
    const [show, setShow] = useState(false);
    const [selectedView, setSelectedView] = useState<string>(() => {
        const stateValue = filterParams?.FILTER?.COL?.find(
            (col: any) => col?.name === "state"
        )?.value;
        return stateValue || "";
    });
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const [viewFilterControls, setViewFilterControls] = useState<
        IFilterControl[]
    >([]);
    const getPersistedFiltersRef = useRef<
        ((viewId: string) => any[] | null) | null
    >(null);
    const resetBaselineRef = useRef<(() => void) | null>(null);
    const didApplyInitialViewRef = useRef<boolean>(false);

    // Memoize the mobile layout condition to prevent unnecessary re-renders
    const shouldUseMobileLayout = deviceInfo.shouldUseMobileLayout();
    const isMobileOrSmallScreen =
        shouldUseMobileLayout || deviceInfo.isIPad || deviceInfo.isTablet;

    const handleClose = () => setShow(false);
    const handleShow = () => {
        setShow(true);
        setSelectedView(
            filterParams?.FILTER?.COL?.find((col: any) => col?.name === "state")
                ?.value || ""
        );
    };

    const createParams = useCallback(
        async (filterTypeName: any, filterValue: any) => {
            const filterValueStr =
                filterTypeName === "view"
                    ? filterValue
                    : Array.from(filterValue).join(",");

            const updateFilterParams = (
                params: FilterParam | FilterParam[]
            ) => {
                // Support both single object and array of objects
                const paramArray = Array.isArray(params) ? params : [params];

                let updatedArray = [...filterParams.FILTER.COL];

                paramArray.forEach(({ name, type, value }: FilterParam) => {
                    // Find existing filter with same name
                    const existingIndex = updatedArray.findIndex(
                        (obj: any) => obj.name === name
                    );
                    if (existingIndex !== -1) {
                        // Update existing filter
                        updatedArray[existingIndex] = {
                            ...updatedArray[existingIndex],
                            type,
                            value,
                        };
                    } else {
                        // Add new filter
                        updatedArray.push({ name, type, value });
                    }
                });
                filterParams.FILTER.COL = updatedArray;
            };

            switch (filterTypeName) {
                case "type":
                    updateFilterParams({
                        name: "type",
                        type: "in",
                        value:
                            filterValueStr === "All types"
                                ? ""
                                : filterValueStr,
                    });
                    break;
                case "view":
                    //remove value of object from filterParams state
                    filterParams.FILTER.COL = filterParams.FILTER.COL.filter(
                        (col: any) =>
                            col.name !== "state" &&
                            col.name !== "responsibleUser"
                    );
                    updateFilterParams({
                        name: "state",
                        type: "in",
                        value: filterValue || "",
                    });
                    break;
                case "customState":
                    updateFilterParams({
                        name: "customState",
                        type: "in",
                        value:
                            filterValueStr === "All states"
                                ? ""
                                : filterValueStr,
                    });
                    break;
                case "user":
                    updateFilterParams({
                        name: "responsibleUser",
                        type: "any",
                        value:
                            filterValueStr === "All users"
                                ? ""
                                : filterValueStr,
                    });
                    break;
                default:
                    break;
            }

            filterParams.FILTER.COL = filterParams.FILTER.COL.filter(
                (col: any) => col.value !== ""
            );
            onFilterChange(filterParams);
        },
        [filterParams, onFilterChange]
    );

    const handleViewSelect = useCallback(
        (view: CustomView | null) => {
            if (view === null) {
                setSelectedView("");
                setViewFilterControls([]);
                // Views-first: clear view selection and any view-driven field/sort config.
                // This prevents any default/hardcoded activity fetch.
                const updatedParams: any = {
                    ...filterParams,
                    FILTER: { COL: [] },
                };
                delete updatedParams.FIELDS;
                delete updatedParams.ORDER;
                onFilterChange(updatedParams);
                return;
            }

            // Custom view
            setSelectedView(view.id || "");

            // Parse view definition and apply filters, FIELDS, and ORDER
            // Reset filterParams to clear previous view's filters
            const updatedParams: FilterParamsState = {
                FILTER: { COL: [] },
            };

            if (view.definition) {
                try {
                    const definition = JSON.parse(view.definition);

                    // Extract FIELDS from columns[].itemname (only visible columns)
                    if (
                        definition.columns &&
                        Array.isArray(definition.columns)
                    ) {
                        const fields = definition.columns
                            .filter((col: any) => col.isVisible !== false)
                            .map((col: any) => col.itemname)
                            .filter(
                                (itemname: any) =>
                                    itemname != null && itemname !== ""
                            );

                        if (fields.length > 0) {
                            updatedParams.FIELDS = { field: fields };
                        } else {
                            delete updatedParams.FIELDS;
                        }
                    } else {
                        delete updatedParams.FIELDS;
                    }

                    // Extract ORDER from sorting[]
                    if (
                        definition.sorting &&
                        Array.isArray(definition.sorting) &&
                        definition.sorting.length > 0
                    ) {
                        const order: Order = {
                            COL: definition.sorting.map((sort: any) => {
                                // If itemname contains '|', split and use the second part
                                let sortName = sort.itemname;
                                if (sortName && sortName.includes("|")) {
                                    const parts = sortName.split("|");
                                    if (
                                        parts.length > 1 &&
                                        parts[1]?.trim() !== ""
                                    ) {
                                        sortName = parts[1].trim();
                                    }
                                }
                                return {
                                    name: sortName,
                                    desc:
                                        sort.level === "Descending" ? "1" : "0",
                                };
                            }),
                        };
                        updatedParams.ORDER = order;
                    } else {
                        delete updatedParams.ORDER;
                    }

                    // Build filter controls from columns where isFilter === true
                    if (
                        definition.columns &&
                        Array.isArray(definition.columns)
                    ) {
                        // Step 1: Get filterable columns (visibility source)
                        const filterableColumns = definition.columns.filter(
                            (col: any) => col?.isFilter === true
                        );

                        // Build a lookup of filters by itemname for default values
                        // Also build lookup by name (handling pipe character) for matching
                        const filtersByItemname: Record<string, any> = {};
                        const filtersByName: Record<string, any> = {};
                        if (Array.isArray(definition.filters)) {
                            definition.filters.forEach((f: any) => {
                                if (f?.itemname) {
                                    filtersByItemname[f.itemname] = f;
                                }
                                // Also index by name (handle pipe character - use part before pipe)
                                if (f?.name) {
                                    let filterName = f.name;
                                    if (filterName.includes("|")) {
                                        const parts = filterName.split("|");
                                        filterName =
                                            parts[0]?.trim() || filterName;
                                    }
                                    filtersByName[filterName] = f;
                                }
                            });
                        }

                        // Step 2: Build controls from filterable columns, resolve defaults from filters[]
                        const controls: IFilterControl[] = filterableColumns
                            .map((column: any) => {
                                if (!column) return null;

                                // Find matching formSetting to get the id for IColumnItem
                                const matchingFormSetting = formSettings?.find(
                                    (item) => item.itemname === column.itemname
                                );
                                const columnId = matchingFormSetting
                                    ? String(matchingFormSetting.id)
                                    : "";

                                // Look up default values from filters section by itemname or name
                                const matchingFilter =
                                    filtersByItemname[column.itemname] ||
                                    filtersByName[column.itemname];

                                // Build filterInfo with actual values from the filter definition
                                let filterInfo: any[] = [
                                    {
                                        name: "value",
                                        type: "equals",
                                        value: "",
                                    },
                                ];

                                if (
                                    matchingFilter?.filterInfo &&
                                    Array.isArray(matchingFilter.filterInfo)
                                ) {
                                    filterInfo = matchingFilter.filterInfo.map(
                                        (fi: any) => {
                                            // Handle isCurrentUser - use runtime userId if flag is set
                                            let filterValue = fi.value || "";
                                            if (
                                                fi.isCurrentUser === true &&
                                                store
                                            ) {
                                                filterValue = String(
                                                    store.UserId
                                                );
                                            }

                                            // Convert is_empty/is_not_empty to proper format
                                            let filterType =
                                                fi.type || "equals";
                                            let negation = fi.negation || false;

                                            if (fi.type === "is_empty") {
                                                filterType = "empty";
                                                negation = false;
                                            } else if (
                                                fi.type === "is_not_empty"
                                            ) {
                                                filterType = "empty";
                                                negation = true;
                                            }

                                            // Derive valueLabel for date presets if missing
                                            let derivedValueLabel = fi.valueLabel;
                                            if (!derivedValueLabel && filterType === "relative_preset" && filterValue) {
                                                // Map preset values to labels (matching DateFilterControl RELATIVE_PRESETS)
                                                const presetLabelMap: Record<string, string> = {
                                                    today: "Today",
                                                    yesterday: "Yesterday",
                                                    tomorrow: "Tomorrow",
                                                    last_7_days: "Last 7 Days",
                                                    next_7_days: "Next 7 Days",
                                                    last_30_days: "Last 30 Days",
                                                    next_30_days: "Next 30 Days",
                                                    last_month: "Last Month",
                                                    this_month: "This Month",
                                                    next_month: "Next Month",
                                                    last_quarter: "Last Quarter",
                                                    this_quarter: "This Quarter",
                                                    next_quarter: "Next Quarter",
                                                    last_year: "Last Year",
                                                    this_year: "This Year",
                                                    next_year: "Next Year",
                                                };
                                                derivedValueLabel = presetLabelMap[filterValue] || filterValue;
                                            }

                                            const preservedFilterInfo = {
                                                name: fi.name || "value",
                                                type: filterType,
                                                value: filterValue,
                                                negation: negation,
                                                includeNull: fi.includeNull,
                                                isCurrentUser: fi.isCurrentUser,
                                                // Preserve valueLabel if present in Custom View definition, or use derived value
                                                ...(derivedValueLabel !== undefined && {
                                                    valueLabel: derivedValueLabel,
                                                }),
                                            };

                                            return preservedFilterInfo;
                                        }
                                    );
                                }

                                const control: IFilterControl = {
                                    name: column.itemname || "",
                                    dataType: column.type || "text",
                                    label:
                                        column.label || column.itemname || "",
                                    data: {
                                        id: columnId,
                                        itemname: column.itemname || "",
                                        label:
                                            column.label ||
                                            column.itemname ||
                                            "",
                                        isVisible: true,
                                        isFilter: true,
                                        type: column.type || "text",
                                    },
                                    filterInfo,
                                };

                                return control;
                            })
                            .filter(
                                (
                                    c: IFilterControl | null
                                ): c is IFilterControl => c !== null
                            );

                        setViewFilterControls(controls);

                        // Apply preset filter values from filters[] to filterParams.FILTER.COL
                        if (Array.isArray(definition.filters)) {
                            definition.filters.forEach((filterDef: any) => {
                                if (!filterDef?.filterInfo?.length) return;

                                const filterInfo = filterDef.filterInfo[0];
                                // Allow is_empty and is_not_empty operators even if value is empty
                                const isEmptyOperator =
                                    filterInfo?.type === "is_empty" ||
                                    filterInfo?.type === "is_not_empty";
                                if (!isEmptyOperator && !filterInfo?.value)
                                    return;

                                // If filter name contains '|', split and use the first part
                                let filterName = filterDef.name;
                                if (filterName && filterName.includes("|")) {
                                    const parts = filterName.split("|");
                                    filterName = parts[0]?.trim() || filterName;
                                }

                                // Convert is_empty/is_not_empty to API format
                                let filterType = filterInfo.type || "equals";
                                let negation = false;

                                if (filterInfo.type === "is_empty") {
                                    filterType = "empty";
                                    negation = false;
                                } else if (filterInfo.type === "is_not_empty") {
                                    filterType = "empty";
                                    negation = true;
                                }

                                // Use runtime userId if isCurrentUser flag is set, otherwise use saved value
                                let filterValue = filterInfo.value || "";
                                if (
                                    filterInfo.isCurrentUser === true &&
                                    store
                                ) {
                                    filterValue = String(store.UserId);
                                }

                                const filterParam: any = {
                                    name: filterName,
                                    type: filterType,
                                    value: filterValue,
                                };

                                // Add negation parameter if true
                                if (negation) {
                                    filterParam.negation = true;
                                }

                                updatedParams.FILTER.COL =
                                    updatedParams.FILTER.COL.filter(
                                        (col: any) =>
                                            col.name !== filterParam.name
                                    );

                                updatedParams.FILTER.COL.push(filterParam);
                            });
                        }
                    } else {
                        setViewFilterControls([]);
                    }
                } catch (error) {
                    console.error("Error parsing view definition:", error);
                    setViewFilterControls([]);
                    delete updatedParams.FIELDS;
                    delete updatedParams.ORDER;
                }
            } else {
                setViewFilterControls([]);
                delete updatedParams.FIELDS;
                delete updatedParams.ORDER;
            }

            // Remove empty filter values (but preserve is_empty/is_not_empty filters which have empty values but type='empty')
            updatedParams.FILTER.COL = updatedParams.FILTER.COL.filter(
                (col: any) =>
                    col.type === "empty" ||
                    (col.value !== "" &&
                        col.value !== null &&
                        col.value !== undefined)
            );

            // Load persisted filter overrides from React state and merge with defaults
            const persistedFilters = getPersistedFiltersRef.current?.(
                view.id || ""
            );
            if (persistedFilters && persistedFilters.length > 0) {
                // Merge persisted filters with defaults (persisted filters take precedence)
                persistedFilters.forEach((persistedFilter: any) => {
                    const existingIndex = updatedParams.FILTER.COL.findIndex(
                        (col: any) => col.name === persistedFilter.name
                    );
                    if (existingIndex !== -1) {
                        // Update existing filter with persisted value
                        updatedParams.FILTER.COL[existingIndex] = {
                            ...updatedParams.FILTER.COL[existingIndex],
                            ...persistedFilter,
                        };
                    } else {
                        // Add persisted filter if it doesn't exist in defaults
                        updatedParams.FILTER.COL.push(persistedFilter);
                    }
                });
            }

            // Add state filter to indicate which custom view is selected
            updatedParams.FILTER.COL.push({
                name: "state",
                type: "equals",
                value: view.id || "",
            });

            // Update filterParams with FIELDS, ORDER, and state filter, then trigger filter change
            onFilterChange(updatedParams);
        },
        [
            filterParams,
            onFilterChange,
            getPersistedFiltersRef,
            formSettings,
            store,
        ]
    );

    // Apply initial custom view selection exactly once (when no view is already selected)
    useEffect(() => {
        if (didApplyInitialViewRef.current) return;

        const currentlySelectedFromParams = filterParams?.FILTER?.COL?.find(
            (col: any) => col?.name === "state"
        )?.value;
        if (currentlySelectedFromParams) {
            didApplyInitialViewRef.current = true;
            return;
        }

        const candidateId = initialViewId ? String(initialViewId).trim() : "";
        if (!candidateId) return;

        const view = customViews.find((v) => String(v.id ?? "") === candidateId);
        if (!view) return;

        didApplyInitialViewRef.current = true;
        handleViewSelect(view);
    }, [customViews, filterParams, handleViewSelect, initialViewId]);

    return isMobileOrSmallScreen ? (
        <View
            position={"relative"}
            width={"100%"}
            height={"64px"}
            UNSAFE_className={styles.mobile_header_parent}
        >
            <Flex
                direction={"column"}
                position={"absolute"}
                top={"calc(50% - 16px)"}
                left={"58px"}
                align-items={"start"}
                justifyContent={"start"}
            >
                <View
                    position={"relative"}
                    UNSAFE_className={styles.mobile_header_text_1}
                >
                    {customViews.find((v) => v.id === selectedView)?.name ||
                        t("all_service_activities", {
                            ns: "layout_components",
                        })}
                </View>
                <View
                    position={"relative"}
                    UNSAFE_className={styles.mobile_header_text_2}
                >
                    {t("service_activities", { ns: "layout_components" })}
                </View>
            </Flex>
            <View
                position={"absolute"}
                top={"calc(50% - 12px)"}
                left={"16px"}
                width={"24px"}
                height={"24px"}
                overflow={"hidden"}
            >
                <span onClick={handleShow} className="me-2">
                    <i className="bi bi-list"></i>
                </span>
                <FilterViewsComponent
                    views={customViews}
                    selectedViewId={selectedView}
                    onViewSelect={handleViewSelect}
                    onCreateView={onCreateView}
                    onLoadMore={onLoadMoreCustomViews}
                    onViewsRefresh={onRefreshCustomViews}
                    hasMore={hasMoreCustomViews}
                    isMobile={true}
                    show={show}
                    onClose={handleClose}
                    onShow={handleShow}
                    store={store}
                    createParams={createParams}
                    formSettings={formSettings}
                    deviceInfo={deviceInfo}
                    filterParams={filterParams}
                    getPersistedFiltersRef={getPersistedFiltersRef}
                    resetBaselineRef={resetBaselineRef}
                />
            </View>

            <FilterControlsComponent
                filterControls={viewFilterControls}
                filterParams={filterParams}
                onFilterChange={onFilterChange}
                createParams={createParams}
                isMobile={true}
                isOpen={isOpen}
                onClose={() => setIsOpen(false)}
                onOpen={() => setIsOpen(true)}
                showDefaultFilters={true}
                fpaTypeCategory={fpaTypeCategory}
                formSettings={formSettings}
                selectedView={customViews.find((v) => v.id === selectedView) || undefined}
                resetBaselineRef={resetBaselineRef}
            />
        </View>
    ) : (
        <View
            width={"100%"}
            position={"relative"}
            backgroundColor={"static-white"}
            UNSAFE_className={styles.filter_menu_parent}
            UNSAFE_style={filterStyle}
        >
            <Flex
                direction={"column"}
                alignItems={"start"}
                gap={"16px"}
                width={"100%"}
            >
                <FilterViewsComponent
                    views={customViews}
                    selectedViewId={selectedView}
                    onViewSelect={handleViewSelect}
                    onCreateView={onCreateView}
                    onLoadMore={onLoadMoreCustomViews}
                    onViewsRefresh={onRefreshCustomViews}
                    hasMore={hasMoreCustomViews}
                    isMobile={false}
                    store={store}
                    createParams={createParams}
                    formSettings={formSettings}
                    deviceInfo={deviceInfo}
                    filterParams={filterParams}
                    getPersistedFiltersRef={getPersistedFiltersRef}
                    resetBaselineRef={resetBaselineRef}
                />
                <FilterControlsComponent
                    filterControls={viewFilterControls}
                    filterParams={filterParams}
                    onFilterChange={onFilterChange}
                    createParams={createParams}
                    isMobile={false}
                    showDefaultFilters={false}
                    fpaTypeCategory={fpaTypeCategory}
                    selectedView={customViews.find((v) => v.id === selectedView) || undefined}
                    resetBaselineRef={resetBaselineRef}
                />
                <QuickFilterControlsComponent
                    formSettings={formSettings}
                    filterParams={filterParams}
                    onFilterChange={onFilterChange}
                    fpaTypeCategory={fpaTypeCategory}
                />
            </Flex>
        </View>
    );
};

export default FilterComponent;
